var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Alert, CircularProgress, Chip, } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { Download as DownloadIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { machineService } from '@services/machineService';
import { formatDateForFilename } from '@utils/dateUtils';
import { businessService } from '@services/businessService';
const AdminMachines = () => {
    const [machines, setMachines] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [exporting, setExporting] = useState(false);
    useEffect(() => {
        loadMachines();
    }, []);
    const loadMachines = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setLoading(true);
            setError(null);
            // Load all businesses and machines
            const businesses = yield businessService.getAll();
            const allMachines = yield Promise.all(businesses.map((business) => __awaiter(void 0, void 0, void 0, function* () {
                const businessMachines = yield machineService.getByBusinessId(business.id);
                return businessMachines.map((machine) => (Object.assign(Object.assign({}, machine), { businessName: business.name })));
            })));
            const flattenedMachines = allMachines.flat();
            setMachines(flattenedMachines);
        }
        catch (err) {
            console.error('Error loading machines:', err);
            setError(err.message || 'Failed to load machines');
        }
        finally {
            setLoading(false);
        }
    });
    const handleExport = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            setExporting(true);
            setError(null);
            const blob = yield machineService.exportToExcel();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `machines_export_${formatDateForFilename()}.xlsx`;
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
            document.body.removeChild(a);
        }
        catch (err) {
            console.error('Error exporting machines:', err);
            setError(err.message || 'Failed to export machines');
        }
        finally {
            setExporting(false);
        }
    });
    const columns = [
        {
            field: 'businessName',
            headerName: 'Business',
            width: 180,
            renderCell: (params) => (_jsx(Chip, { label: params.value, color: "primary", variant: "outlined", size: "small" })),
        },
        {
            field: 'name',
            headerName: 'Machine Name',
            width: 200,
            renderCell: (params) => (_jsx(Typography, { variant: "body2", fontWeight: "bold", children: params.value })),
        },
        {
            field: 'description',
            headerName: 'Description',
            width: 200,
        },
        {
            field: 'yearModel',
            headerName: 'Year',
            width: 100,
        },
        {
            field: 'model',
            headerName: 'Model',
            width: 150,
        },
        {
            field: 'vin',
            headerName: 'VIN',
            width: 180,
        },
        {
            field: 'engineNumber',
            headerName: 'Engine #',
            width: 150,
        },
        {
            field: 'rego',
            headerName: 'Registration',
            width: 130,
        },
        {
            field: 'notes',
            headerName: 'Notes',
            width: 200,
        },
        {
            field: 'createdAt',
            headerName: 'Created',
            width: 130,
            valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '',
        },
        {
            field: 'updatedAt',
            headerName: 'Updated',
            width: 130,
            valueFormatter: (value) => value ? new Date(value).toLocaleDateString() : '',
        },
    ];
    return (_jsxs(Box, { children: [_jsxs(Box, { sx: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }, children: [_jsx(Typography, { variant: "h6", children: "All Machines" }), _jsxs(Box, { sx: { display: 'flex', gap: 1 }, children: [_jsx(Button, { variant: "outlined", startIcon: _jsx(RefreshIcon, {}), onClick: loadMachines, disabled: loading, children: "Refresh" }), _jsx(Button, { variant: "contained", startIcon: _jsx(DownloadIcon, {}), onClick: handleExport, disabled: exporting || machines.length === 0, children: exporting ? 'Exporting...' : 'Export to Excel' })] })] }), error && (_jsx(Alert, { severity: "error", sx: { mb: 2 }, onClose: () => setError(null), children: error })), _jsx(Alert, { severity: "info", sx: { mb: 2 }, children: "Comprehensive view of all machines across all businesses. Use the filter and sort features in the grid, then export filtered/sorted results to Excel." }), loading ? (_jsx(Box, { sx: { display: 'flex', justifyContent: 'center', py: 4 }, children: _jsx(CircularProgress, {}) })) : (_jsx(Box, { sx: { height: 600, width: '100%' }, children: _jsx(DataGrid, { rows: machines, columns: columns, pageSizeOptions: [25, 50, 100], initialState: {
                        pagination: {
                            paginationModel: { pageSize: 25 },
                        },
                    }, slots: { toolbar: GridToolbar }, slotProps: {
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }, disableRowSelectionOnClick: true }) })), _jsx(Box, { sx: { mt: 2 }, children: _jsxs(Typography, { variant: "caption", color: "text.secondary", children: ["Total Machines: ", machines.length] }) })] }));
};
export default AdminMachines;
